import React, { useEffect, useState } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import {
  Box,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  ListItemButton,
  useMediaQuery,
  Drawer,
  Typography,
} from "@mui/material";
import AssignmentIcon from "@mui/icons-material/Assignment";
import ChatIcon from "@mui/icons-material/Chat";
import BookmarkIcon from "@mui/icons-material/Bookmark";
import OtherHousesIcon from "@mui/icons-material/OtherHouses";
import QueryStatsIcon from "@mui/icons-material/QueryStats";
import GavelIcon from "@mui/icons-material/Gavel";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import AdminPanelSettingsIcon from "@mui/icons-material/AdminPanelSettings";
import ExtensionOutlinedIcon from "@mui/icons-material/ExtensionOutlined";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faBook,
  faFile,
  faFileContract,
  faFileLines,
  faListCheck,
  faFileSignature,
} from "@fortawesome/free-solid-svg-icons";
import { useSelector, useDispatch } from "react-redux";

import { getPlanDetails } from "../../services/api/getAllPlans";

const COLLAPSED_WIDTH = "72px";
const EXPANDED_WIDTH = "256px";

const MCP_MENU_LABEL = "MCP Connector";

const menuItems = [
  {
    label: "Home",
    path: "/app/home",
    icon: <OtherHousesIcon fontSize='small' />,
  },
  {
    label: "Legal Chat",
    path: "/app/legal-chat",
    icon: <ChatIcon fontSize='small' />,
  },
  {
    label: "Case Search",
    path: "/app/searchResults",
    icon: <GavelIcon fontSize='small' />,
  },
  {
    label: "Statute Search",
    path: "/app/statute-search",
    icon: <MenuBookOutlinedIcon fontSize='small' />,
  },
  {
    label: "Bookmarks",
    path: "/app/bookmarks",
    icon: <BookmarkIcon fontSize='small' />,
  },
  {
    label: "Application Drafting",
    path: "/app/application-drafting",
    icon: <FontAwesomeIcon icon={faFileSignature} fontSize='18px' />,
  },
  {
    label: "Petition Drafting",
    path: "/app/petition-drafting",
    icon: <AssignmentIcon fontSize='small' />,
  },
  {
    label: "Contract Drafting",
    path: "/app/contract-drafting",
    icon: <FontAwesomeIcon icon={faFileLines} fontSize='18px' />,
  },
  {
    label: "Legal Library",
    path: "/app/legal-library",
    icon: <FontAwesomeIcon icon={faBook} fontSize='18px' />,
  },
  {
    label: "Case Management System",
    path: "/app/case-management",
    icon: <FontAwesomeIcon icon={faListCheck} fontSize='18px' />,
  },
  {
    label: "Document Analysis",
    path: "/app/document-analysis",
    icon: <QueryStatsIcon fontSize='small' />,
  },
  {
    label: "Document Chat",
    path: "/app/document-chat",
    icon: <FontAwesomeIcon icon={faFile} fontSize='18px' />,
  },
  {
    label: "Contract Analysis",
    path: "/app/contract-analysis",
    icon: <FontAwesomeIcon icon={faFileContract} fontSize='18px' />,
  },
  {
    label: MCP_MENU_LABEL,
    path: "/app/mcp-connector",
    icon: <ExtensionOutlinedIcon fontSize='small' />,
  },
];

const HomeSidebar = ({ isOpen, onClose }) => {
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const location = useLocation();

  // Get user info from Redux store
  const userInfo = useSelector((state) => state.userLogin?.userInfo);
  console.log("userInfo in home sidebar", userInfo);
  const isAdmin = userInfo?.role === "admin";
  console.log("isadmin", isAdmin);

  // Combine static menu items with Admin-only items
  const allMenuItems = [
    ...menuItems,
    ...(isAdmin
      ? [
          {
            label: "Signup Requests",
            path: "/app/signup-requests",
            icon: <AdminPanelSettingsIcon fontSize='small' />,
          },
        ]
      : []),
  ];

  const [selectedItem, setSelectedItem] = useState("Home");
  const [isHovered, setIsHovered] = useState(false);
  const [planDetails, setPlanDetails] = useState(null);
  const [planLoading, setPlanLoading] = useState(true);

  const isMobile = useMediaQuery((theme) => theme.breakpoints.down("lg"));
  const isExpanded = isOpen || isHovered;

  useEffect(() => {
    const fetchUserPlan = async () => {
      try {
        setPlanLoading(true);

        if (!userInfo || !userInfo?.currentPlanId) {
          setPlanDetails(null);
          return;
        }

        const response = await getPlanDetails(userInfo.currentPlanId);
        const data = response?.data?.data || response?.data;

        setPlanDetails(data || null);
      } catch (error) {
        console.error("Failed to fetch plan details:", error);
        setPlanDetails(null);
      } finally {
        setPlanLoading(false);
      }
    };

    fetchUserPlan();
  }, [userInfo?.currentPlanId]);

  useEffect(() => {
    const activeMenuItem = allMenuItems.find(
      (item) => item.path === location.pathname,
    );
    if (activeMenuItem) {
      setSelectedItem(activeMenuItem.label);
    }
  }, [location.pathname, isAdmin]);

  const handleItemClick = (item) => {
    if (onClose) onClose();
    setIsHovered(false);
    setSelectedItem(item.label);
    navigate(item.path);
  };

  const visibleMenuItems = allMenuItems.filter((item) => {
    // Keep 'Home' and 'Signup Requests' visible without checking subscription plan modules
    if (
      item.label === "Home" ||
      item.label === "Signup Requests" ||
      item.label === MCP_MENU_LABEL
    ) {
      return true;
    }

    if (!planDetails) {
      return false;
    }

    return planDetails.modules?.includes(item.label);
  });

  const isActive = (item) => selectedItem === item.label;

  const sidebarContent = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        backgroundColor: "#FFFFFF",
        borderRight: "1px solid #E5E7EB",
        width: isExpanded ? EXPANDED_WIDTH : COLLAPSED_WIDTH,
        transition: "width 0.2s ease-in-out",
        overflow: "hidden",
      }}
    >
      <Box sx={{ flex: 1, overflowY: "auto", overflowX: "hidden", py: 2 }}>
        <List sx={{ px: isExpanded ? 2 : 1 }}>
          {visibleMenuItems.map((item) => (
            <ListItem key={item.label} disablePadding sx={{ mb: 0.25 }}>
              <ListItemButton
                onClick={() => handleItemClick(item)}
                selected={isActive(item)}
                sx={{
                  borderRadius: "8px",
                  px: isExpanded ? 2 : 1,
                  py: 1.5,
                  color: isActive(item) ? "#0073A6" : "#374151",
                  backgroundColor: isActive(item) ? "#F0F9FF" : "transparent",
                  justifyContent: isExpanded ? "flex-start" : "center",
                  "&:hover": {
                    backgroundColor: isActive(item) ? "#F0F9FF" : "#F9FAFB",
                    color: isActive(item) ? "#0073A6" : "#111827",
                  },
                  "&.Mui-selected": {
                    backgroundColor: "#F0F9FF",
                    "&:hover": { backgroundColor: "#F0F9FF" },
                  },
                }}
              >
                <ListItemIcon
                  sx={{
                    minWidth: isExpanded ? "40px" : "auto",
                    color: isActive(item) ? "#0073A6" : "#6B7280",
                    justifyContent: "center",
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                {isExpanded && (
                  <ListItemText
                    primary={item.label}
                    sx={{
                      "& .MuiListItemText-primary": {
                        fontSize: "14px",
                        fontWeight: isActive(item) ? 500 : 400,
                        whiteSpace: "nowrap",
                      },
                    }}
                  />
                )}
              </ListItemButton>
            </ListItem>
          ))}
        </List>
      </Box>

      {isExpanded && (
        <Box sx={{ px: 3, py: 3, borderTop: "1px solid #F3F4F6" }}>
          <Typography
            variant='caption'
            sx={{ color: "#9CA3AF", fontSize: "12px" }}
          >
            © 2026 LawGPT
          </Typography>
        </Box>
      )}
    </Box>
  );

  if (isMobile) {
    return (
      <Drawer
        id='home-sidebar-mobile'
        anchor='left'
        open={isOpen}
        onClose={onClose}
        sx={{
          "& .MuiDrawer-paper": {
            width: EXPANDED_WIDTH,
            boxSizing: "border-box",
            border: "none",
            boxShadow: "4px 0 12px rgba(0, 0, 0, 0.1)",
          },
        }}
      >
        {sidebarContent}
      </Drawer>
    );
  }

  return (
    <Box
      id='home-sidebar-main'
      onMouseEnter={() => setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
      sx={{
        position: "fixed",
        top: "64px",
        left: 0,
        height: "calc(100vh - 64px)",
        zIndex: 1200,
        transition: "width 0.2s ease-in-out",
        boxShadow:
          isHovered && !isOpen ? "4px 0 16px rgba(0,0,0,0.10)" : "none",
      }}
    >
      {sidebarContent}
    </Box>
  );
};

export default HomeSidebar;
